import { bugsnagClient } from '@Services/observability/initObservability';
import {
  ANNUAL_PLAN_VARIATION_ID_TO_KEY,
  ExperienceMap,
  type RawConvertData,
  type RawConvertVariation,
  type ResolvedVariationKey,
} from './types';
import { getVariationKeyFromRawKey, isValidExperienceId } from './util';

interface ParseExperimentsParams {
  convertData?: RawConvertData;
}

/**
 * Resolves the variation key from a raw Convert variation.
 *
 * Legacy experiences expose the variation as a `key` (e.g. `"123-variation-1"`). Newer
 * experiences such as the annual-plan A/B (LC-28827) only expose the variation `id`, so they are
 * resolved from the id first — keeping `control`/`treatment` consistent with the value sent on
 * review submit. Returns `null` when the variation cannot be resolved.
 */
const resolveVariationKey = (
  experienceId: string,
  variation: RawConvertVariation
): ResolvedVariationKey | null => {
  if (experienceId === ExperienceMap.ANNUAL_PLAN && variation.id) {
    const annualPlanKey =
      ANNUAL_PLAN_VARIATION_ID_TO_KEY[variation.id as keyof typeof ANNUAL_PLAN_VARIATION_ID_TO_KEY];
    if (annualPlanKey) {
      return annualPlanKey;
    }
  }

  if (variation.key) {
    return getVariationKeyFromRawKey(variation.key);
  }

  return null;
};

export const parseExperiments = ({ convertData }: ParseExperimentsParams) => {
  if (!convertData?.experiences) {
    return [];
  }

  return Object.entries(convertData.experiences)
    .map(([experienceId, data]) => {
      const variation = data.variation;
      if (!variation || !variation.id) {
        bugsnagClient?.notify(
          new Error(`Experience ${experienceId} has malformed variation data: ${variation}`)
        );
        return null;
      }
      if (!isValidExperienceId(experienceId)) {
        bugsnagClient?.notify(new Error(`Experience ${experienceId} is not a valid experience ID`));
        return null;
      }

      const variationKey = resolveVariationKey(experienceId, variation);
      if (!variationKey) {
        bugsnagClient?.notify(
          new Error(
            `Variation ${variation.key ?? variation.id} is not a valid variation key for experience ${experienceId}`
          )
        );
        return null;
      }
      return {
        id: experienceId,
        variationKey,
      };
    })
    .filter((experience) => experience !== null);
};
